import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { LayoutDashboard, Briefcase, Activity, Video, FileText, Calendar, Users, DollarSign, Settings, MessageSquare, Mail, GraduationCap, UserCog, Image, FolderArchive, Radio, AtSign, KeyRound, FileEdit } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import DashboardStats from '@/components/admin/DashboardStats';
import VisitStats from '@/components/admin/VisitStats';
import ProjectsManager from '@/components/admin/ProjectsManager';
import ActivitiesManager from '@/components/admin/ActivitiesManager';
import VideosManager from '@/components/admin/VideosManager';
import BlogManager from '@/components/admin/BlogManager';
import CalendarManager from '@/components/admin/CalendarManager';
import MembersManager from '@/components/admin/MembersManager';
import DonationsManager from '@/components/admin/DonationsManager';
import SettingsManager from '@/components/admin/SettingsManager';
import ChatManager from '@/components/admin/ChatManager';
import CorrespondenceManager from '@/components/admin/CorrespondenceManager';
import TrainingManager from '@/components/admin/TrainingManager';
import FormationsManager from '@/components/admin/FormationsManager';
import JobsManager from '@/components/admin/JobsManager';
import PhotosManager from '@/components/admin/PhotosManager';
import DocumentsManager from '@/components/admin/DocumentsManager';
import NewsManager from '@/components/admin/NewsManager';
import EmailRequestsManager from '@/components/admin/EmailRequestsManager';
import ChangePasswordForm from '@/components/ChangePasswordForm';
import PageContentManager from '@/components/admin/PageContentManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data.role !== 'admin') {
        navigate('/');
        return;
      }
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'projects', label: 'Projets', icon: Briefcase },
    { id: 'activities', label: 'Activités', icon: Activity },
    { id: 'videos', label: 'Vidéos', icon: Video },
    { id: 'photos', label: 'Galerie photos', icon: Image },
    { id: 'documents', label: 'Documents & Rapports', icon: FolderArchive },
    { id: 'news', label: "Bande-annonce d'actualités", icon: Radio },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'calendar', label: 'Calendrier', icon: Calendar },
    { id: 'members', label: 'Adhérents', icon: Users },
    { id: 'donations', label: 'Dons', icon: DollarSign },
    { id: 'training', label: 'Demandes de formation', icon: GraduationCap },
    { id: 'formations_pub', label: 'Formations FPT à publier', icon: GraduationCap },
    { id: 'jobs', label: 'Recrutement', icon: UserCog },
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'correspondence', label: 'Correspondance', icon: Mail },
    { id: 'email_requests', label: 'Demandes e-mails CRESA', icon: AtSign },
    { id: 'page_content', label: 'Contenu des pages', icon: FileEdit },
    { id: 'settings', label: 'Paramètres', icon: Settings },
    { id: 'account', label: 'Mon compte', icon: KeyRound },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <><DashboardStats /><VisitStats /></>;
      case 'projects': return <ProjectsManager />;
      case 'activities': return <ActivitiesManager />;
      case 'videos': return <VideosManager />;
      case 'photos': return <PhotosManager />;
      case 'documents': return <DocumentsManager />;
      case 'news': return <NewsManager />;
      case 'blog': return <BlogManager />;
      case 'calendar': return <CalendarManager />;
      case 'members': return <MembersManager />;
      case 'donations': return <DonationsManager />;
      case 'training': return <TrainingManager />;
      case 'formations_pub': return <FormationsManager />;
      case 'jobs': return <JobsManager />;
      case 'chat': return <ChatManager />;
      case 'correspondence': return <CorrespondenceManager />;
      case 'email_requests': return <EmailRequestsManager />;
      case 'page_content': return <PageContentManager />;
      case 'settings': return <SettingsManager />;
      case 'account': return (
        <div className="max-w-xl">
          <ChangePasswordForm />
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster />
      <Navigation />

      <section className="section-padding bg-[#F9F8F6]" data-testid="admin-page">
        <div className="container mx-auto px-6">
          <div className="mb-8">
            <h1 
              className="text-4xl font-bold text-[#2E332D] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="admin-title"
            >
              Espace Administrateur
            </h1>
            <p className="text-[#5C665A]">Bienvenue, {user?.name} • Gérez tous les contenus du site</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
            {/* Sidebar */}
            <aside className="card h-fit lg:sticky lg:top-24">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`admin-tab-${tab.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-[#4A6B48] text-white'
                        : 'text-[#5C665A] hover:bg-[#4A6B48]/10 hover:text-[#4A6B48]'
                    }`}
                  >
                    <tab.icon size={18} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <main className="card">
              {renderContent()}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
